import { Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { HomeCardLayout } from "@/campaigns";
import { missionRewardPrize } from "@/campaigns/modules/missions/types";
import type { SprintMission } from "@/campaigns/modules/sprint-mission/types";
import {
  poolUrgency,
  prizesRemaining,
} from "@/campaigns/modules/sprint-mission/types";
import { CompactCampaignCard } from "@/components/campaign-card/campaign-card-compact";
import { formatPrizeTitle } from "@/utils/helpers";

/**
 * Home-feed card — a thin wrapper around the shared `CompactCampaignCard`. The hook line under
 * the image is the one prize, like every other campaign card; the scarcity rides the artwork as
 * the corner badge ("37 winners left", red-worded "Only 5 left" when urgent, "All winners are
 * in" once the pool is empty). Vocabulary matches the detail view (#3958).
 */
export const SprintMissionHomeCard = ({
  sprintMission,
  layout,
}: {
  sprintMission: SprintMission;
  layout: HomeCardLayout;
}) => {
  const { t } = useTranslation();
  const remaining = prizesRemaining(sprintMission);
  const urgency = poolUrgency(sprintMission);
  const exhausted = urgency === "gone";
  const prize = sprintMission.mission
    ? missionRewardPrize(sprintMission.mission)
    : null;

  const scarcity = () => {
    if (remaining === null) return undefined;
    if (exhausted) return t("sprintMission.allWinnersIn", "All winners are in");
    if (urgency === "urgent") {
      return t("sprintMission.onlyLeft", "Only {{count}} left", {
        count: remaining,
      });
    }
    return t("sprintMission.winnersLeftCount", "{{count}} winners left", {
      count: remaining,
    });
  };

  return (
    <CompactCampaignCard
      campaign={sprintMission.campaign}
      // The same key as the standard missions card: one campaign-type word, translated once.
      typeLabel={t("missions.typeLabel", "Missions")}
      typeIcon={<Trophy />}
      hookValue={prize ? formatPrizeTitle(prize.title, t) : null}
      hookLabel={t("sprintMission.oneTimePrize", "One time prize")}
      hookIsCatalogItem={prize?.is_catalog_item}
      metaExtra={scarcity()}
      layout={layout}
    />
  );
};
